import {
  ALL_CHART_TYPES,
  BLACK_HEX,
  DEFAULT_LINE_THICKNESS,
  DEFAULT_NORMALIZE_BY,
  DEFAULT_NORMALIZE_BY_SETTING,
  PARAM_FIELD
} from "constants/chart.constants";
import { CalculationMethod, EvaSource, TimeStep } from "constants/charts.enums";
import { createMergeWithDefaults, createStateWithDefaults } from "entities/utils";
import { StackedBarChartRequestType, StackedBarChartSettings } from "types";

import { MaxBinsSortOrderEnum } from "models/binSize";
import { EntityKind } from "models/entityKind";

import { PdenSourceEnum } from "../../../../models/pdenDataSourceSetting";
import { BASE_CHART_REQUEST } from "./defaults";

// Same stacked-bar data as StackedBar, drawn rotated — see
// translators/stackedBarHorizontal.ts. chartTypeParam's axis roles are
// swapped (x = numeric, y = category), but that's a UI-only convention: the
// fields below (xVariable/yVariable, xField/yField) are WIRE-shaped, same as
// every other chart type — xField/xVariable is always the bucket dimension,
// yField/yVariable always the numeric one. useChartDataController.ts
// recomputes both from chartTypeParam via getWireOrientedChartTypeParam
// (proxies.ts) on every real request; these statics are just the
// pre-first-fetch placeholder and must keep the wire shape, not chartTypeParam's.
export const DEFAULT_STACKED_BAR_HORIZONTAL_CHART_SETTINGS: Partial<StackedBarChartSettings> =
  {
    product: "Oil",
    otherProducts: [],
    chartType: ALL_CHART_TYPES.StackedBarHorizontal.id,
    showForecast: true,
    showWellCount: true,
    averageSettings: {
      color: BLACK_HEX,
      thickness: DEFAULT_LINE_THICKNESS
    },
    showAverage: false,
    p10Settings: {
      color: BLACK_HEX,
      thickness: DEFAULT_LINE_THICKNESS
    },
    showP10: false,
    p50Settings: {
      color: BLACK_HEX,
      thickness: DEFAULT_LINE_THICKNESS
    },
    showP50: false,
    p90Settings: {
      color: BLACK_HEX,
      thickness: DEFAULT_LINE_THICKNESS
    },
    showP90: false,
    pdenDataSourceSetting: {
      source: EvaSource.Public,
      timeStep: TimeStep.Month
    },
    forecastMode: "forecastTrump",
    useForecastStartDate: false,
    survivorBias: true,
    rateType: 0,
    cutoff: 50,
    timeStep: TimeStep.Month,
    shutInMonths: 3,
    source: EvaSource.Public,
    normalizeBy: DEFAULT_NORMALIZE_BY,
    useWeightedRatioAverage: true,
    xVariable: {
      valueProperty: "Dates.Vintage_Year",
      displayTitle: "Vintage",
      canBin: true,
      dataType: "Integer",
      mappingName: "Dates.Vintage_Year",
      bin: null,
      pdenSource: PdenSourceEnum.Public.toString()
    },
    yVariable: {
      valueProperty: PARAM_FIELD.OilCumToDate.property,
      displayTitle: PARAM_FIELD.OilCumToDate.title,
      canBin: PARAM_FIELD.OilCumToDate.canBin,
      dataType: PARAM_FIELD.OilCumToDate.dataType,
      mappingName: PARAM_FIELD.OilCumToDate.property,
      pdenSource: PdenSourceEnum.Public.toString()
    },
    lockUnits: false,
    stackedBarCalculation: CalculationMethod.Count,
    pressureChartSettings: {
      product: "Oil",
      productivityIndex: false
    },
    movingAverageDays: null,
    annotationSettings: {},
    splitYearActive: false
  };

export const DEFAULT_STACKED_BAR_HORIZONTAL_CHART_REQUEST: Partial<StackedBarChartRequestType> =
  {
    ...BASE_CHART_REQUEST,
    FilterId: "",
    requestId: "",
    ReverseColor: false,
    ColorPalette: {
      id: "-6",
      name: "Portland",
      shared: false,
      colors: ["rgb(12,51,131)"],
      preferredColors: [],
      paletteType: 0,
      colorScale: [],
      specialColors: null,
      thickness: [2],
      reverse: false
    },
    GroupBy: {
      title: "Resource Play",
      property: "Header.ResourcePlay",
      groupByField: "Header.ResourcePlay",
      pdenSource: "Public",
      canBin: false,
      dataType: "Text",
      categoryId: 1,
      tooltip: "",
      display: "",
      entityKind: EntityKind.Well,
      bin: {
        BinSize: 0,
        MinSize: null,
        MaxBins: null,
        MaxBinsSortOrder: MaxBinsSortOrderEnum.WellCount,
        GreaterThan: null,
        LessThan: null,
        Quantile: {
          quantileType: "NumberOfQuantile",
          numQuantiles: 4
        },
        BinType: "BinSize",
        UseDynamicBins: false
      },
      mode: undefined,
      forecastTrumpingSettings: undefined,
      performanceDateRange: undefined
    },
    sortBy: "AlphabeticalAsc",
    userForecast: [],
    userForecastSettings: {
      forecastSourceFolder: null,
      excludeMcDanielForecasts: false,
      reserveCategory: null,
      excludeNoviForecasts: true,
      specialForecastFolders: ["McDaniel Research"]
    },
    forecastMode: "forecastTrump",
    useForecastStartDate: false,
    showGroupsNotInFilter: true,
    placeholderBinsEnabled: false,
    hasCheckedBins: true,
    syncClientPden: false,
    syncClientSchema: "",
    colorLockedItems: {},
    syncClientFcst: false,
    syncForecastRescat: "2111",
    normalizeBySetting: DEFAULT_NORMALIZE_BY_SETTING,
    chartSetting:
      DEFAULT_STACKED_BAR_HORIZONTAL_CHART_SETTINGS as StackedBarChartSettings,
    chartFieldInformation: {
      groupByField: {
        property: "Header.ResourcePlay",
        title: "Resource Play",
        type: "groupBy",
        defaultUnit: "",
        folderName: "",
        isForecast: false,
        product: "",
        performanceDateRange: undefined
      },
      xField: {
        property: "Dates.Vintage_Year",
        title: "Vintage",
        type: "x",
        defaultUnit: "",
        product: "",
        pdenSource: PdenSourceEnum.Public,
        folderName: "",
        isForecast: false,
        performanceDateRange: undefined
      },
      yField: {
        property: PARAM_FIELD.OilCumToDate.property,
        title: PARAM_FIELD.OilCumToDate.title,
        type: "y",
        defaultUnit: PARAM_FIELD.OilCumToDate.defaultUnit,
        product: PARAM_FIELD.OilCumToDate.product,
        pdenSource: PdenSourceEnum.Public,
        folderName: "",
        isForecast: false,
        performanceDateRange: undefined
      }
    },
    axisMinMax: {
      xMin: null,
      xMax: null,
      yMin: null,
      yMax: null
    },
    isDefaultLockOn: true
  };

export const generateStackedBarHorizontalRequestBody = createMergeWithDefaults(
  DEFAULT_STACKED_BAR_HORIZONTAL_CHART_REQUEST
);

export const generateStackedBarHorizontalChartSettings = createStateWithDefaults(
  DEFAULT_STACKED_BAR_HORIZONTAL_CHART_SETTINGS
);
